import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from './AdminLayout';
import { getDashboardStats } from '../../services/apiService';

const Dashboard = () => {
  const [userEmail, setUserEmail] = useState('');
  const [stats, setStats] = useState({
    news: 0,
    events: 0,
    comments: 0,
    resources: 0,
    partners: 0,
    deliverables: 0,
    completedDeliverables: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    
    // Charger les statistiques depuis l'API
    const loadStats = async () => {
      try {
        setIsLoading(true);
        const data = await getDashboardStats();
        if (data && data.stats) {
          setStats({
            news: data.stats.news ?? 0,
            events: data.stats.events ?? 0,
            comments: data.stats.comments ?? 0,
            resources: data.stats.resources ?? 0,
            partners: data.stats.partners ?? 0,
            deliverables: data.stats.deliverables ?? 0,
            completedDeliverables: data.stats.completedDeliverables ?? 0
          });
          setRecentActivity(data.recentActivity || []);
        } else {
          // Valeurs par défaut si l'API ne retourne pas de données
          setStats({
            news: 0,
            events: 0,
            comments: 0,
            resources: 0,
            partners: 0,
            deliverables: 0,
            completedDeliverables: 0
          });
          setRecentActivity([]);
        }
      } catch (error) {
        // Valeurs par défaut en cas d'erreur
        setStats({
          news: 0,
          events: 0,
          comments: 0,
          resources: 0,
          partners: 0,
          deliverables: 0,
          completedDeliverables: 0
        });
        setRecentActivity([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadStats();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  return (
    <AdminLayout>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: '-30%',
          right: '-15%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-20%',
          left: '-10%',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{ 
            fontSize: 'clamp(24px, 3vw, 36px)', 
            fontWeight: '700', 
            margin: '0 0 12px 0',
            color: 'white'
          }}>
            Welcome to Administration
          </h1>
          <p style={{
            fontSize: '16px',
            margin: '0 0 20px 0',
            opacity: 0.9,
            fontWeight: '400'
          }}>
            Manage your MedAgriCycle platform with ease and efficiency
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {[
          {
            title: 'News',
            value: isLoading ? '...' : (stats.news ?? 0).toString(),
            icon: 'fas fa-newspaper',
            color: '#96ca2d',
            bgColor: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/news'
          },
          {
            title: 'Events',
            value: isLoading ? '...' : (stats.events ?? 0).toString(),
            icon: 'fas fa-calendar-alt',
            color: '#96ca2d',
            bgColor: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/events'
          },
          {
            title: 'Comments',
            value: isLoading ? '...' : (stats.comments ?? 0).toString(),
            icon: 'fas fa-comments',
            color: '#96ca2d',
            bgColor: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/comments'
          },
          {
            title: 'Resources',
            value: isLoading ? '...' : (stats.resources ?? 0).toString(),
            icon: 'fas fa-folder-open',
            color: '#96ca2d',
            bgColor: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/resources'
          },
          {
            title: 'Partners',
            value: isLoading ? '...' : (stats.partners ?? 0).toString(),
            icon: 'fas fa-handshake',
            color: '#96ca2d',
            bgColor: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/partners'
          },
          {
            title: 'Deliverables',
            value: isLoading ? '...' : (stats.deliverables ?? 0).toString(),
            icon: 'fas fa-file-alt',
            color: '#96ca2d',
            bgColor: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/deliverables'
          },
          {
            title: 'Completed Deliverables',
            value: isLoading ? '...' : (stats.completedDeliverables ?? 0).toString(),
            icon: 'fas fa-check-circle',
            color: '#10b981',
            bgColor: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            change: '',
            changeType: 'positive',
            path: '/admin/deliverables'
          }
        ].map((stat, index) => (
          <div 
            key={index} 
            onClick={() => stat.path && navigate(stat.path)}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(150, 202, 45, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.borderColor = 'rgba(150, 202, 45, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.borderColor = 'rgba(150, 202, 45, 0.1)';
            }}
          >
            {/* Background Gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '3px',
              background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
              transform: 'translateY(0px)',
              borderColor: 'rgba(150, 202, 45, 0.1)'
            }} />
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: stat.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '20px'
              }}>
                <i className={stat.icon} />
              </div>
              {stat.change && (
                <div style={{
                  background: stat.changeType === 'positive' ? '#dcfce7' : '#fef2f2',
                  color: stat.changeType === 'positive' ? '#166534' : '#991b1b',
                  padding: '4px 10px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  <i className={`fas fa-arrow-${stat.changeType === 'positive' ? 'up' : 'down'}`} style={{ marginRight: '3px' }} />
                  {stat.change}
                </div>
              )}
            </div>
            
            <h3 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 6px 0',
              color: '#1f2937'
            }}>
              {stat.value}
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0,
              fontWeight: '500'
            }}>
              {stat.title}
            </p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '700',
          margin: '0 0 20px 0',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <i className="fas fa-bolt" style={{ color: '#96ca2d' }} />
          Quick Actions
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {[
            { title: 'New News', icon: 'fas fa-newspaper', color: '#96ca2d', path: '/admin/news' },
            { title: 'New Event', icon: 'fas fa-calendar-alt', color: '#96ca2d', path: '/admin/events' },
            { title: 'Moderate Comments', icon: 'fas fa-comments', color: '#96ca2d', path: '/admin/comments' },
            { title: 'Manage Resources', icon: 'fas fa-folder-open', color: '#96ca2d', path: '/admin/resources' }
          ].map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = action.color;
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${action.color} 0%, #7fb800 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <i className={action.icon} />
              </div>
              <span style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151'
              }}>
                {action.title}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '700',
          margin: '0 0 20px 0',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <i className="fas fa-history" style={{ color: '#96ca2d' }} />
          Recent Activity
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
              Loading recent activity...
            </div>
          ) : recentActivity.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              No recent activity
            </div>
          ) : (
            recentActivity.map((activity, index) => {
              // Déterminer le chemin selon le type
              let activityPath = '';
              if (activity.type === 'news') {
                activityPath = '/admin/news';
              } else if (activity.type === 'event') {
                activityPath = '/admin/events';
              } else if (activity.type === 'comment') {
                activityPath = '/admin/comments';
              } else if (activity.type === 'resource') {
                activityPath = '/admin/resources';
              }
              
              return (
            <div 
              key={index} 
              onClick={() => activityPath && navigate(activityPath)}
              style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              cursor: activityPath ? 'pointer' : 'default'
            }}
            onMouseEnter={(e) => {
              if (activityPath) {
                e.target.style.borderColor = 'rgba(150, 202, 45, 0.2)';
                e.target.style.transform = 'translateX(2px)';
                e.target.style.background = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (activityPath) {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.transform = 'translateX(0)';
                e.target.style.background = '#f9fafb';
              }
            }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${activity.color} 0%, #7fb800 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px'
              }}>
                <i className={activity.icon} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: '0 0 2px 0',
                  color: '#1f2937'
                }}>
                  {activity.action}
                </p>
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  {activity.time}
                </p>
              </div>
            </div>
            );
            })
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;